/**
 * Mode B: all the phone numbers, a blank line, all the promo codes (PLAN §9).
 *
 * The most dangerous input in the program. One extra line in the first block
 * shifts every pair below it, and every shifted row still looks perfectly
 * valid — the phone is a real phone, the code is a real code, and the customer
 * gets somebody else's discount. Nothing here guesses, nothing truncates to the
 * shorter list, and everything it notices it says out loud.
 *
 * Pure by construction (PLAN §4).
 */

import { isValidPhone } from './phone.ts';
import { SEPARATORS, toLines, type Line, type ParsedPair } from './recipients.ts';

export type TwoBlockProblem =
  /** Nothing pasted yet. */
  | 'empty'
  /** No blank line: one block, so there is nothing to pair. */
  | 'one-block'
  /** Three or more blocks — a hard error rather than a guess about which two to use. */
  | 'too-many-blocks'
  /** Two blocks of different lengths. Never resolved by truncating. */
  | 'count-mismatch';

export type TwoBlockWarning =
  /** The second block looks like the phones and the first does not. Offer a swap. */
  | 'blocks-reversed'
  /** Both blocks look like phone numbers: the same column pasted twice. */
  | 'both-blocks-phones'
  /** A line carries `;` `,` tab or `|` — probably paired lines pasted into the wrong mode. */
  | 'separator-in-lines';

export interface TwoBlockOutcome {
  readonly ok: boolean;
  readonly problem?: TwoBlockProblem;
  readonly blocks: number;
  /** For the live counters — `Номеров: 150 · Промокодов: 148` — which must show even when refused. */
  readonly phoneCount: number;
  readonly codeCount: number;
  /**
   * Pairs for as far as the two blocks agree, so the aligned preview can be
   * drawn even while the counts are wrong. Never sendable unless `ok`.
   */
  readonly pairs: readonly ParsedPair[];
  /** The tail of the longer block, for highlighting. */
  readonly unpaired: readonly Line[];
  readonly unpairedIn?: 'phones' | 'codes';
  readonly warnings: readonly TwoBlockWarning[];
}

/** Above this share of a block parsing as phone numbers, the block *is* phone numbers. */
const MOSTLY = 0.8;

/** Below this, it clearly is not. Between the two, nothing is claimed either way. */
const HARDLY = 0.5;

export function parseTwoBlocks(text: string): TwoBlockOutcome {
  const blocks = splitOnBlankLines(text);

  if (blocks.length === 0) return refuse('empty', blocks);
  if (blocks.length === 1) return refuse('one-block', blocks);
  if (blocks.length > 2) {
    // A stray blank line inside the phone list must never silently become a
    // block boundary, so this is reported rather than resolved.
    return refuse('too-many-blocks', blocks);
  }

  const [phones = [], codes = []] = blocks;
  const paired = Math.min(phones.length, codes.length);
  const warnings = warningsFor(phones, codes);

  const pairs: ParsedPair[] = [];
  for (let index = 0; index < paired; index += 1) {
    pairs.push({
      // The phone's own line number: it is the anchor an error message can
      // point at, and the one the administrator can count to in what they
      // pasted.
      line: (phones[index] as Line).number,
      phone: (phones[index] as Line).text,
      promoCode: (codes[index] as Line).text,
    });
  }

  const mismatch = phones.length !== codes.length;
  return {
    ok: !mismatch,
    ...(mismatch ? { problem: 'count-mismatch' as const } : {}),
    blocks: 2,
    phoneCount: phones.length,
    codeCount: codes.length,
    pairs,
    unpaired: phones.length > codes.length ? phones.slice(paired) : codes.slice(paired),
    ...(mismatch
      ? { unpairedIn: phones.length > codes.length ? ('phones' as const) : ('codes' as const) }
      : {}),
    warnings,
  };
}

/**
 * Runs of blank lines separate blocks, whether the run is one line or five, and
 * leading or trailing blank lines produce no block at all. Line numbers survive
 * the split, so trimming a trailing newline cannot shift what an error points at.
 */
export function splitOnBlankLines(text: string): readonly (readonly Line[])[] {
  const blocks: Line[][] = [];
  let current: Line[] = [];

  for (const line of toLines(text)) {
    if (line.text === '') {
      if (current.length > 0) blocks.push(current);
      current = [];
      continue;
    }
    current.push(line);
  }
  if (current.length > 0) blocks.push(current);

  return blocks;
}

function warningsFor(phones: readonly Line[], codes: readonly Line[]): readonly TwoBlockWarning[] {
  const warnings: TwoBlockWarning[] = [];
  const first = phoneShare(phones);
  const second = phoneShare(codes);

  if (first >= MOSTLY && second >= MOSTLY) {
    // The same column pasted twice: every "promo code" is a phone number.
    warnings.push('both-blocks-phones');
  } else if (first < HARDLY && second >= MOSTLY) {
    warnings.push('blocks-reversed');
  }

  if ([...phones, ...codes].some(containsSeparator)) {
    // Paired lines pasted into the wrong mode. Flagged, never split on.
    warnings.push('separator-in-lines');
  }
  return warnings;
}

function phoneShare(lines: readonly Line[]): number {
  if (lines.length === 0) return 0;
  const valid = lines.filter((line) => isValidPhone(line.text)).length;
  return valid / lines.length;
}

function containsSeparator(line: Line): boolean {
  return Object.values(SEPARATORS).some((separator) => line.text.includes(separator));
}

function refuse(problem: TwoBlockProblem, blocks: readonly (readonly Line[])[]): TwoBlockOutcome {
  return {
    ok: false,
    problem,
    blocks: blocks.length,
    // Counts still come back for the live display: with one block or three,
    // "how many lines did I paste?" is exactly the question being asked.
    phoneCount: blocks[0]?.length ?? 0,
    codeCount: blocks[1]?.length ?? 0,
    pairs: [],
    unpaired: [],
    warnings: [],
  };
}
